type ErrorVariant = "authnEmailConstraint";

/**
 * Represents a server error based on the `wayflyer:status-code` meta tag.
 *
 * While the backend still serves the SPA by template, it may throw a genuine HTTP error for whatever reason. This
 * gets baked into the HTML as a meta tag, which we can then read here and handle in-SPA.
 */
export default class MetaServerError extends Error {
  status: number;
  variant?: ErrorVariant;

  constructor(status: number, variant?: ErrorVariant) {
    super(`Server error with status code: ${status}`);
    this.name = "MetaServerError";
    this.status = status;
    this.variant = variant;
  }

  /**
   * Checks if the `wayflyer:status-code` meta tag exists and returns a new `MetaServerError` instance if the status
   * code is not OK.
   */
  static fromMeta() {
    const statusMeta = document.querySelector("meta[name='wayflyer:status-code']");
    if (statusMeta) {
      const statusMetaContent = statusMeta.getAttribute("content");
      if (!statusMetaContent) {
        return null;
      }

      const statusCode = parseInt(statusMetaContent);
      if (isNaN(statusCode)) {
        return null;
      }

      if (!(statusCode >= 200 && statusCode < 300)) {
        const variantMeta = document.querySelector("meta[name='wayflyer:error-message-variant']");
        if (variantMeta) {
          const variantMetaContent = variantMeta.getAttribute("content");
          if (variantMetaContent) {
            return new MetaServerError(statusCode, variantMetaContent as ErrorVariant);
          }
        }
        return new MetaServerError(statusCode);
      }
    }

    return null;
  }

  /**
   * Removes the `wayflyer:status-code` meta tag from the document.
   */
  static cleanup() {
    const statusMeta = document.querySelector("meta[name='wayflyer:status-code']");
    if (statusMeta) {
      statusMeta.remove();
    }
  }
}
